import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import { config } from 'config/config';
import type { UniversalMapView } from 'lib/universalSearchSession';
import type { FeatureCollection, Point } from 'geojson';
import { UniversalMapCasino, casinoBounds, casinoMapFeatures, topMapCasinos } from 'lib/universalSearchView';
import { fitOverview, safeFitBounds } from 'lib/universalSearchMapFit';

mapboxgl.accessToken = config.mapbox.key;

// Same world style + DOM pin look as the Casino Selection map (NearbyCasinosMap).
const WORLD_STYLE_URL = 'mapbox://styles/mapbox/dark-v11';
const WORLD_VIEW: UniversalMapView = { lng: -98.5, lat: 39.5, zoom: 2.4 };
const TOP_CASINO_ZOOM = 8;
// Initial (no search yet) fit around every casino: don't zoom past this for one / close casinos.
const OVERVIEW_MAX_ZOOM = 6;

// Mapbox clustering (GeoJSON source, cluster: true). Pins that are close at the
// current zoom merge into a gold count bubble (sum of matching EGMs); tapping it
// zooms in until the pins split. The top-match casino is never clustered.
const SOURCE_ID = 'us-casinos';
const CLUSTER_LAYER = 'us-clusters';
const CLUSTER_COUNT_LAYER = 'us-cluster-count';
const POINT_LAYER = 'us-points';
const CLUSTER_RADIUS = 48;
const CLUSTER_MAX_ZOOM = 9;

type Props = {
  /** Search results, or (overview) every casino the search covers. */
  casinos: UniversalMapCasino[];
  /**
   * No search yet: plain pins for every casino (no counts), popup with the
   * name only, and the map fits around all of them.
   */
  overview?: boolean;
  /** Casino with the most matching EGMs (gold; never clustered). */
  centerCasinoId: number | null;
  /** After a search, keep this many top casinos (by matching EGMs) in view (config, default 3). */
  topCasinos?: number;
  /** Restore this view instead of centering (globe icon return). */
  initialView?: UniversalMapView | null;
  /** Bumped on every new search so the map re-centers on the new top casino. */
  searchSeq: number;
  /** Drawer height at its start stop, so the centered pin sits in the visible map. */
  bottomPadding?: number;
  /** App header height, so the centered pin is not under the header. */
  topPadding?: number;
  onSelectCasino: (casinoId: number) => void;
  onViewChange?: (view: UniversalMapView) => void;
  onPinTap?: (casinoId: number) => void;
  onClusterTap?: (clusterSize: number) => void;
};

const buildPopup = (c: UniversalMapCasino, onSelect: () => void, overview: boolean): HTMLElement => {
  // DOM nodes (not HTML strings) so names from the database render as text.
  const root = document.createElement('div');
  root.className = 'nearby-casinos-map__popup';
  const title = document.createElement('div');
  title.className = 'nearby-casinos-map__popup-name';
  title.textContent = c.displayName;
  if (overview) {
    // Before a search: name (+ city) only; entering a casino stays on Casino Selection.
    const place = [c.city, c.state].filter(Boolean).join(', ');
    root.append(title);
    if (place) {
      const where = document.createElement('div');
      where.className = 'nearby-casinos-map__popup-distance';
      where.textContent = place;
      root.append(where);
    }
    return root;
  }
  const meta = document.createElement('div');
  meta.className = 'nearby-casinos-map__popup-distance';
  meta.textContent = `${c.count} matching EGM${c.count === 1 ? '' : 's'}`;
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'nearby-casinos-map__popup-select';
  button.textContent = 'Show results';
  button.addEventListener('click', onSelect);
  root.append(title, meta, button);
  return root;
};

const buildPin = (c: UniversalMapCasino, isTop: boolean, overview: boolean): HTMLElement => {
  const pinEl = document.createElement('div');
  pinEl.className = `nearby-casinos-map__pin us-map__pin${isTop ? ' nearby-casinos-map__pin--nearest' : ''}`;
  pinEl.setAttribute('aria-label', overview ? c.displayName : `${c.displayName}: ${c.count} matching EGMs`);
  const head = document.createElement('span');
  head.className = 'nearby-casinos-map__pin-head';
  pinEl.appendChild(head);
  if (overview) return pinEl;
  const badge = document.createElement('span');
  badge.className = 'us-map__count';
  badge.textContent = String(c.count);
  pinEl.appendChild(badge);
  return pinEl;
};

export const UniversalSearchMap = ({
  casinos,
  overview = false,
  centerCasinoId,
  topCasinos = 3,
  initialView,
  searchSeq,
  bottomPadding = 0,
  topPadding = 0,
  onSelectCasino,
  onViewChange,
  onPinTap,
  onClusterTap,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const loadedRef = useRef(false);
  const cb = useRef({ onSelectCasino, onViewChange, onPinTap, onClusterTap });
  cb.current = { onSelectCasino, onViewChange, onPinTap, onClusterTap };
  const casinosRef = useRef(casinos);
  casinosRef.current = casinos;
  const centerRef = useRef(centerCasinoId);
  centerRef.current = centerCasinoId;
  const overviewRef = useRef(overview);
  overviewRef.current = overview;
  // Overview fit runs once per overview stretch (again after the search is cleared).
  const overviewFittedRef = useRef(false);
  const paddingRef = useRef({ top: topPadding, bottom: bottomPadding });
  paddingRef.current = { top: topPadding, bottom: bottomPadding };
  // DOM markers by casino id (top pin + currently unclustered pins).
  const markersRef = useRef<Map<number, mapboxgl.Marker>>(new Map());
  // The restored view applies once (first search after mount); later searches re-center.
  const restoreRef = useRef<UniversalMapView | null>(initialView || null);
  const lastCenteredSeqRef = useRef<number>(-1);

  /** Show DOM pins for the top casino + every point Mapbox has not clustered. */
  const syncMarkers = () => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    const byId = new Map(casinosRef.current.map((c) => [c.casinoId, c]));
    const want = new Set<number>();
    const top = centerRef.current;
    if (top !== null && byId.get(top)?.location) want.add(top);
    if (map.getSource(SOURCE_ID)) {
      for (const f of map.querySourceFeatures(SOURCE_ID)) {
        const p = f.properties as { cluster?: boolean; casinoId?: number } | null;
        if (p && !p.cluster && typeof p.casinoId === 'number') want.add(p.casinoId);
      }
    }
    const markers = markersRef.current;
    markers.forEach((m, id) => {
      if (!want.has(id)) {
        m.remove();
        markers.delete(id);
      }
    });
    want.forEach((id) => {
      if (markers.has(id)) return;
      const c = byId.get(id);
      if (!c || !c.location) return;
      const el = buildPin(c, id === top, overviewRef.current);
      el.addEventListener('click', () => cb.current.onPinTap?.(id));
      const popup = new mapboxgl.Popup({ offset: 18, closeButton: false, maxWidth: '220px' }).setDOMContent(
        buildPopup(c, () => cb.current.onSelectCasino(c.casinoId), overviewRef.current)
      );
      markers.set(
        id,
        new mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([c.location.lng, c.location.lat])
          .setPopup(popup)
          .addTo(map)
      );
    });
  };
  const syncRef = useRef(syncMarkers);
  syncRef.current = syncMarkers;

  const applyData = () => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    const src = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    // Rebuild every pin (counts / top casino change between searches).
    markersRef.current.forEach((m) => m.remove());
    markersRef.current.clear();
    src?.setData(casinoMapFeatures(casinosRef.current, centerRef.current) as unknown as FeatureCollection);
    syncRef.current();
  };
  const applyRef = useRef(applyData);
  applyRef.current = applyData;

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const start = restoreRef.current || WORLD_VIEW;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: WORLD_STYLE_URL,
      center: [start.lng, start.lat],
      zoom: start.zoom,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    });
    map.addControl(new mapboxgl.AttributionControl({ compact: true }));
    map.touchZoomRotate.disableRotation();
    map.on('load', () => {
      map.resize();
      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: casinoMapFeatures([], null) as unknown as FeatureCollection,
        cluster: true,
        clusterRadius: CLUSTER_RADIUS,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        clusterProperties: { egms: ['+', ['get', 'count']] },
      });
      map.addLayer({
        id: CLUSTER_LAYER,
        type: 'circle',
        source: SOURCE_ID,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': 'rgba(32, 28, 29, 0.9)',
          'circle-stroke-color': '#E5BA66',
          'circle-stroke-width': 2,
          'circle-radius': ['step', ['get', 'point_count'], 20, 3, 24, 8, 28],
        },
      });
      map.addLayer({
        id: CLUSTER_COUNT_LAYER,
        type: 'symbol',
        source: SOURCE_ID,
        filter: ['has', 'point_count'],
        layout: {
          // Results: "EGMs\nN casinos". Overview (no counts): "N\ncasinos".
          'text-field': [
            'case',
            ['>', ['get', 'egms'], 0],
            ['concat', ['to-string', ['get', 'egms']], '\n', ['to-string', ['get', 'point_count']], ' casinos'],
            ['concat', ['to-string', ['get', 'point_count']], '\n', 'casinos'],
          ],
          'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
          'text-size': ['step', ['get', 'point_count'], 11, 8, 12],
          'text-line-height': 1.1,
          'text-allow-overlap': true,
        },
        paint: { 'text-color': '#FFFFFF' },
      });
      // Invisible layer so unclustered points are loaded for querySourceFeatures (DOM pins).
      map.addLayer({
        id: POINT_LAYER,
        type: 'circle',
        source: SOURCE_ID,
        filter: ['!', ['has', 'point_count']],
        paint: { 'circle-radius': 1, 'circle-opacity': 0 },
      });
      map.on('click', CLUSTER_LAYER, (e) => {
        const f = map.queryRenderedFeatures(e.point, { layers: [CLUSTER_LAYER] })[0];
        if (!f) return;
        const clusterId = (f.properties as { cluster_id?: number })?.cluster_id;
        const size = Number((f.properties as { point_count?: number })?.point_count) || 0;
        cb.current.onClusterTap?.(size);
        const src = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource;
        if (clusterId === undefined) return;
        src.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err || zoom === null || zoom === undefined) return;
          map.easeTo({ center: (f.geometry as Point).coordinates as [number, number], zoom: zoom + 0.25, duration: 500 });
        });
      });
      map.on('mouseenter', CLUSTER_LAYER, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', CLUSTER_LAYER, () => {
        map.getCanvas().style.cursor = '';
      });
      loadedRef.current = true;
      applyRef.current();
    });
    map.on('moveend', () => {
      const c = map.getCenter();
      cb.current.onViewChange?.({ lng: c.lng, lat: c.lat, zoom: map.getZoom() });
      syncRef.current();
    });
    map.on('sourcedata', (e) => {
      const ev = e as unknown as { sourceId?: string; isSourceLoaded?: boolean };
      if (ev.sourceId === SOURCE_ID && ev.isSourceLoaded) syncRef.current();
    });
    mapRef.current = map;
    const markers = markersRef.current;
    return () => {
      markers.forEach((m) => m.remove());
      markers.clear();
      loadedRef.current = false;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    applyRef.current();
    if (overview) {
      // No search yet: fit around every casino (once), unless a view is being restored.
      if (overviewFittedRef.current || restoreRef.current) return;
      const bounds = casinoBounds(casinos);
      if (!bounds) return;
      overviewFittedRef.current = true;
      const pad = paddingRef.current;
      const run = () => fitOverview(map, bounds, pad, { maxZoom: OVERVIEW_MAX_ZOOM, world: WORLD_VIEW });
      // fitBounds needs the real canvas size (first render can precede map.resize()).
      if (loadedRef.current) run();
      else map.once('load', run);
      return;
    }
    overviewFittedRef.current = false;
    if (lastCenteredSeqRef.current === searchSeq) return;
    lastCenteredSeqRef.current = searchSeq;
    const restore = restoreRef.current;
    restoreRef.current = null;
    if (restore) {
      map.jumpTo({ center: [restore.lng, restore.lat], zoom: restore.zoom });
      return;
    }
    // Keep the top casinos (gold #1 + the next ones by matching EGMs) in view,
    // above the drawer and below the search bar. Never throws (safeFitBounds);
    // falls back to centering on #1.
    const top = topMapCasinos(casinos, centerCasinoId, topCasinos);
    const bounds = casinoBounds(top);
    const first = top[0];
    if (bounds && first && first.location) {
      safeFitBounds(map, bounds, paddingRef.current, {
        maxZoom: TOP_CASINO_ZOOM,
        duration: 900,
        fallback: { lng: first.location.lng, lat: first.location.lat, zoom: TOP_CASINO_ZOOM },
      });
    }
  }, [casinos, centerCasinoId, searchSeq, overview]);

  return <div ref={containerRef} className="nearby-casinos-map us-map" data-testid="universal-search-map" />;
};

export default UniversalSearchMap;
